import React from 'react';
import { buildMediaNote, buildSourceNote, formatInText, mapCitationToSourceMeta } from '../../lib/apaSlideFormatter.js';
import ApaSegments from '../../components/ApaSegments';

const ApaCitationRenderer = ({ block }) => {
  const citations = Array.isArray(block.citations) ? block.citations : [];
  const formatted = citations
    .map((citation) => {
      const meta = mapCitationToSourceMeta(citation);
      if (meta.sourceType === 'clipart_builtin') {
        return null;
      }
      // A figure or table cites itself as a note — italic "Note.", the licence
      // linked to its deed. Everything else takes the parenthetical form.
      const note = buildMediaNote(meta);
      if (note) return { segments: note.segments, meta };
      const text = formatInText(meta);
      return text ? { segments: [{ text }], meta } : null;
    })
    .filter(Boolean);

  // A citation detached from an image or a video renders as a bare, single-row
  // figure note with no card chrome or background, so it sits cleanly beneath
  // the media it came from as its own movable object.
  if (block.variant === 'figure-note') {
    const notes = citations.map(buildSourceNote).filter(Boolean);
    return (
      <div className="apa-figure-note text-sm text-slate-600 leading-snug">
        {notes.length > 0 ? (
          notes.map((note, index) => (
            <div key={`figure-note-${block.id || 'apa'}-${index}`}>
              <ApaSegments segments={note.segments} />
              {note.link && (
                <a
                  href={note.link}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-2 text-indigo-600 hover:text-indigo-800 hover:underline"
                >
                  {note.link}
                </a>
              )}
            </div>
          ))
        ) : (
          <span className="text-slate-400">Add citation details for this source.</span>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
      {formatted.length > 0 ? (
        <div className="space-y-2 text-sm text-slate-700">
          {formatted.map((item, index) => {
            const link = item.meta.doi ? `https://doi.org/${item.meta.doi}` : item.meta.url;
            return (
              <div key={`citation-${block.id || 'apa'}-${index}`}>
                <ApaSegments segments={item.segments} />
                {link && (
                  <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {link}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          Add citation details to generate an in-text citation.
        </p>
      )}
    </div>
  );
};

export default ApaCitationRenderer;
